import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../ui/select';
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from '../../ui/form';
import { DocumentRichText } from '../../documents/form/inputs/DocumentRichText';
import ImageUpload from '../../general-components/ImageUpload';
import FilePicker from '../../ui-components/pickers/FilePicker';
import PolicyPicker from '../../ui-components/pickers/PolicyPicker';
import { useFileUrl } from '../useFileUrl';
import { EQUIPMENT_TYPES, EquipmentValues } from '../types';

const EMPTY: EquipmentValues = {
    name: '',
    type: '',
    notes: '',
    image: null,
    files: [],
    policies: [],
};

interface EquipmentItemFormProps {
    initial?: EquipmentValues;
    bare?: boolean;
    isSaving?: boolean;
    onSave: (values: EquipmentValues) => void;
    onCancel?: () => void;
}

export default function EquipmentItemForm({
    initial,
    bare = false,
    isSaving = false,
    onSave,
    onCancel,
}: EquipmentItemFormProps) {
    const { t } = useTranslation();
    const isEdit = !!initial;
    const form = useForm<EquipmentValues>({ defaultValues: initial ?? EMPTY });
    const image = form.watch('image');
    const imageUrl = useFileUrl(image ?? undefined);

    const reset = () => form.reset(EMPTY);

    const submit = form.handleSubmit((values) => {
        onSave(values);
        if (!isEdit) reset();
    });

    return (
        <Form {...form}>
            <div
                className={cn(
                    bare
                        ? 'rounded-lg border border-subtle bg-card p-4'
                        : 'overflow-hidden rounded-lg border border-subtle bg-card'
                )}
            >
                {!bare && (
                    <div className="border-b border-subtle bg-muted px-4 py-2.5 text-sm font-medium">
                        {t(
                            isEdit
                                ? 'roomOverview.equipment.form.editItem'
                                : 'roomOverview.equipment.form.newItem'
                        )}
                    </div>
                )}

                <div className={cn('space-y-4', !bare && 'p-4')}>
                    <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_10rem]">
                        <div className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                rules={{
                                    required: t(
                                        'roomOverview.equipment.form.nameRequired'
                                    ),
                                }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-xs" required>
                                            {t(
                                                'roomOverview.equipment.form.name'
                                            )}
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className="border-control bg-background"
                                                placeholder={t(
                                                    'roomOverview.equipment.form.namePlaceholder'
                                                )}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="type"
                                rules={{
                                    required: t(
                                        'roomOverview.equipment.form.typeRequired'
                                    ),
                                }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-xs" required>
                                            {t(
                                                'roomOverview.equipment.form.type'
                                            )}
                                        </FormLabel>
                                        <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                        >
                                            <FormControl>
                                                <SelectTrigger className="border-control bg-background">
                                                    <SelectValue
                                                        placeholder={t(
                                                            'roomOverview.equipment.form.typePlaceholder'
                                                        )}
                                                    />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {EQUIPMENT_TYPES.map((type) => (
                                                    <SelectItem
                                                        key={type}
                                                        value={type}
                                                    >
                                                        {type}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormItem>
                            <FormLabel className="text-xs">
                                {t('roomOverview.equipment.form.addImage')}
                            </FormLabel>
                            <div className="aspect-[4/3] w-full">
                                <ImageUpload
                                    compact
                                    className="h-full"
                                    imageUrl={imageUrl ?? undefined}
                                    onImageUploaded={(fileId) =>
                                        form.setValue('image', fileId)
                                    }
                                    labels={{
                                        dragDrop: t(
                                            'roomOverview.equipment.form.imageDragDrop'
                                        ),
                                        dropHere: t(
                                            'roomOverview.equipment.form.imageDropHere'
                                        ),
                                        formats: t(
                                            'roomOverview.equipment.form.imageFormats'
                                        ),
                                    }}
                                />
                            </div>
                        </FormItem>
                    </div>

                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-xs">
                                    {t('roomOverview.equipment.form.notes')}
                                </FormLabel>
                                <FormControl>
                                    <DocumentRichText
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={t(
                                            'roomOverview.equipment.form.notesPlaceholder'
                                        )}
                                        className="overflow-hidden border-control bg-background [&>div:first-child]:bg-input [&_.ProseMirror]:min-h-14"
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <div className="space-y-4 border-t border-subtle pt-4">
                        <FormField
                            control={form.control}
                            name="policies"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-xs">
                                        {t(
                                            'roomOverview.equipment.form.policies'
                                        )}
                                    </FormLabel>
                                    <PolicyPicker
                                        compact
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="files"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-xs">
                                        {t('roomOverview.equipment.form.files')}
                                    </FormLabel>
                                    <FilePicker
                                        compact
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="flex items-center justify-end gap-2 border-t border-subtle pt-4">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-subtle-foreground hover:text-foreground"
                            onClick={onCancel ?? reset}
                        >
                            {t(
                                onCancel
                                    ? 'roomOverview.equipment.form.cancel'
                                    : 'roomOverview.equipment.form.clear'
                            )}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            disabled={isSaving}
                            onClick={submit}
                        >
                            {t(
                                isEdit
                                    ? 'roomOverview.equipment.form.saveItem'
                                    : 'roomOverview.equipment.form.addItem'
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
}
